import type { SupportedLocale } from '@/i18n'
import type { BrandDocMeta } from '@/types/brand-doc-meta'
import type { RunEntry, SetupRun } from '@/types/setup-run'
import type { FactBasis, FactSource } from '@/types/setup-source'

export interface TextFact {
  readonly value: string | null
  readonly sources: readonly FactSource[]
  readonly basis: FactBasis
}

export interface RegionCountry {
  readonly code: string
  readonly sources: readonly FactSource[]
}

export interface RegionArea {
  readonly code: string
  readonly sources: readonly FactSource[]
}

export interface RegionFact {
  readonly countries: readonly RegionCountry[]
  readonly regions: readonly RegionArea[]
  readonly basis: FactBasis
}

export interface FactPerson {
  readonly name: string
  readonly role: string
  readonly sources: readonly FactSource[]
}

export interface PeopleFact {
  readonly people: readonly FactPerson[]
  readonly basis: FactBasis
}

export type BrandKind = 'product' | 'service' | 'both'

export interface BrandFactsPage {
  readonly url: string
  readonly title: string
}

const BRAND_FACT_TEXT_FIELDS = [
  'brandInCategory',
  'category',
  'problemItSolves',
  'targetCustomer',
  'categoryDefinition',
  'approach',
] as const

export type BrandFactTextField = (typeof BRAND_FACT_TEXT_FIELDS)[number]

export type BrandFactField = BrandFactTextField | 'region' | 'people'

export interface BrandFacts {
  readonly meta: BrandDocMeta
  readonly websiteUrl: string
  readonly linkedInUrl: string
  readonly brandInCategory: TextFact
  readonly category: TextFact
  readonly problemItSolves: TextFact
  readonly targetCustomer: TextFact
  readonly categoryDefinition: TextFact
  readonly approach: TextFact
  readonly region: RegionFact
  readonly people: PeopleFact
  readonly kind: BrandKind | null
  readonly language: SupportedLocale
  readonly readAt: string | null
  readonly pages: readonly BrandFactsPage[]
  /** The read run that wrote the sheet. */
  readonly runId: string
}

export const BRAND_FACTS_RUN_STAGES = ['website', 'look', 'extract'] as const

export type BrandFactsRunStage = (typeof BRAND_FACTS_RUN_STAGES)[number]

export interface BrandFactsPageEntry extends RunEntry {
  readonly url: string
  readonly title?: string
}

export interface BrandFactsRun extends SetupRun<BrandFactsRunStage, BrandFactsPageEntry> {
  readonly websiteUrl: string
  readonly linkedInUrl: string
  readonly keepEdited: boolean
  readonly fieldsFilled: number
  /** The website read this run waits on, or `null` before it is started. */
  readonly websiteReadRunId: string | null
}

export type BrandFactsUpdate =
  | { readonly field: BrandFactTextField; readonly value: string }
  | {
      readonly field: 'region'
      readonly countries: readonly string[]
      readonly regions: readonly string[]
    }
  | {
      readonly field: 'people'
      readonly people: readonly { readonly name: string; readonly role: string }[]
    }

export function isFactFilled(fact: TextFact | RegionFact | PeopleFact): boolean {
  if ('value' in fact) return fact.value !== null && fact.value.trim() !== ''
  if ('countries' in fact) return fact.countries.length > 0 || fact.regions.length > 0
  return fact.people.length > 0
}

export function isBrandFactsApproved(facts: BrandFacts): boolean {
  return facts.meta.status === 'approved'
}
